import React, { useEffect, useState } from "react";

/**
 * Componente para impressão de pedidos em formato 58mm
 * Otimizado para impressoras térmicas
 */
const PrintOrderReceipt = () => {
  const [order, setOrder] = useState(null);

  useEffect(() => {
    // Buscar dados do pedido do localStorage
    const orderData = localStorage.getItem("printOrder");
    if (orderData) {
      try {
        const parsedOrder = JSON.parse(orderData);
        setOrder(parsedOrder);
        // Limpar localStorage após carregar
        localStorage.removeItem("printOrder");
      } catch (error) {
        console.error("Erro ao carregar dados do pedido:", error);
      }
    }
  }, []);

  useEffect(() => {
    // Disparar impressão automaticamente quando o pedido estiver carregado
    if (order) {
      // Aguardar um pouco para garantir que o conteúdo foi renderizado
      const timer = setTimeout(() => {
        window.print();
        // Fechar a janela após a impressão (opcional)
        // window.onafterprint = () => window.close();
      }, 800);

      return () => clearTimeout(timer);
    }
  }, [order]);

  if (!order) {
    return (
      <div className="loading-container">
        <div style={{ textAlign: "center" }}>
          <p>Carregando pedido para impressão...</p>
          <p style={{ fontSize: "12px", marginTop: "10px", color: "#666" }}>
            Se esta mensagem persistir, verifique se há dados do pedido.
          </p>
          <button
            onClick={() => window.close()}
            style={{
              marginTop: "20px",
              padding: "10px 20px",
              background: "#c9332C",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Fechar
          </button>
        </div>
      </div>
    );
  }

  // Funções auxiliares
  const centerText = (text, width = 32) => {
    const padding = Math.max(0, Math.floor((width - text.length) / 2));
    return " ".repeat(padding) + text;
  };

  const separator = "=".repeat(32);
  const dashedLine = "-".repeat(32);

  const formatDate = (dateString) => {
    if (!dateString) return new Date().toLocaleString("pt-BR");
    return new Date(dateString).toLocaleString("pt-BR");
  };

  // Calcular valores
  const calculateProductsSubtotal = () => {
    return (
      order.products?.reduce(
        (sum, product) => sum + parseFloat(product.total_value || 0),
        0
      ) || 0
    );
  };

  const getDiscountAmount = () => {
    const productsTotal = calculateProductsSubtotal();
    const discountPercentage = parseFloat(order.discount_percentage || 0);
    return productsTotal * (discountPercentage / 100);
  };

  const calculateTotal = () => {
    const productsTotal = calculateProductsSubtotal();
    const discountAmount = getDiscountAmount();
    const freightValue = parseFloat(order.freight_value || 0);
    return productsTotal - discountAmount + freightValue;
  };

  // Formatar endereço
  const formatAddress = () => {
    if (order.delivery_type?.toLowerCase() !== "delivery") {
      return null;
    }

    const addressObjects = [
      order.address,
      order.delivery_address,
      order.client,
      order.client?.address,
      order.client?.delivery_address,
    ].filter((src) => src && typeof src === "object");

    const pickValue = (keys) => {
      for (const obj of addressObjects) {
        for (const key of keys) {
          if (obj[key]) return obj[key];
        }
      }
      return "";
    };

    const street = pickValue(["logradouro", "street", "rua", "address"]);
    const number = pickValue(["numero", "number", "num"]);
    const complement = pickValue(["complemento", "complement"]);
    const neighborhood = pickValue(["bairro", "neighborhood", "district"]);
    const city = pickValue(["cidade", "city"]);
    const state = pickValue(["estado", "state", "uf"]);
    const cep = pickValue(["cep", "zip", "zipcode"]);
    const reference = pickValue(["referencia", "reference"]);

    const parts = [];
    if (street) {
      let line = street;
      if (number) line += `, Nº ${number}`;
      if (complement) line += ` (${complement})`;
      parts.push(line);
    }
    if (neighborhood) parts.push(`Bairro: ${neighborhood}`);
    if (city || state) parts.push([city, state].filter(Boolean).join(" - "));
    if (cep) parts.push(`CEP: ${cep}`);
    if (reference) parts.push(`Ref.: ${reference}`);

    return parts.filter(Boolean);
  };

  const productsSubtotal = calculateProductsSubtotal();
  const discountAmount = getDiscountAmount();
  const freightValue = parseFloat(order.freight_value || 0);
  const total = calculateTotal();
  const addressLines = formatAddress();

  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Courier New', Courier, monospace;
          font-size: 10px;
          line-height: 1.3;
          color: #000;
          background: #fff;
          width: 58mm;
          margin: 0 auto;
          padding: 2mm;
        }

        .receipt-container {
          width: 100%;
          max-width: 58mm;
        }

        .header {
          text-align: center;
          margin-bottom: 3mm;
        }

        .header h1 {
          font-size: 14px;
          font-weight: bold;
          margin: 1mm 0;
          letter-spacing: 0.5px;
        }

        .header h2 {
          font-size: 11px;
          margin: 1mm 0;
        }

        .separator {
          border-top: 1px solid #000;
          margin: 2mm 0;
        }

        .dashed {
          border-top: 1px dashed #000;
          margin: 2mm 0;
        }

        .section {
          margin: 2mm 0;
        }

        .section-title {
          font-weight: bold;
          font-size: 11px;
          margin: 2mm 0 1mm 0;
          text-transform: uppercase;
        }

        .info-line {
          display: flex;
          justify-content: space-between;
          margin: 1mm 0;
          font-size: 10px;
        }

        .info-label {
          font-weight: bold;
          flex-shrink: 0;
        }

        .info-value {
          text-align: right;
          word-break: break-word;
          max-width: 60%;
        }

        .product-item {
          margin: 2mm 0;
          padding: 1mm 0;
          border-bottom: 1px dashed #000;
        }

        .product-name {
          font-weight: bold;
          margin-bottom: 1mm;
          font-size: 10px;
        }

        .product-detail {
          font-size: 9px;
          margin-left: 2mm;
          margin-top: 0.5mm;
        }

        .sub-item {
          margin-left: 3mm;
          font-size: 9px;
          margin-top: 0.5mm;
        }

        .observation {
          padding: 1mm;
          margin-top: 1mm;
          font-size: 9px;
          border: 1px dashed #000;
        }

        .totals {
          margin-top: 2mm;
        }

        .total-line {
          display: flex;
          justify-content: space-between;
          margin: 1mm 0;
          font-size: 10px;
        }

        .total-line.highlight {
          font-weight: bold;
          font-size: 12px;
          padding-top: 2mm;
          margin-top: 2mm;
          border-top: 2px solid #000;
        }

        .footer {
          text-align: center;
          margin-top: 3mm;
          font-size: 10px;
        }

        .address-section {
          padding: 1mm;
          margin: 2mm 0;
          border: 1px dashed #000;
        }

        .address-line {
          font-size: 9px;
          margin: 0.5mm 0;
          word-wrap: break-word;
        }

        .coupon-badge {
          border: 1px solid #000;
          padding: 0.5mm 1mm;
          font-size: 8px;
          display: inline-block;
          margin-left: 1mm;
          font-weight: bold;
        }

        .loading-container {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100vh;
          font-family: Arial, sans-serif;
        }

        .print-actions {
          position: fixed;
          bottom: 20px;
          right: 20px;
          display: flex;
          gap: 10px;
          z-index: 1000;
        }

        .print-actions button {
          padding: 10px 20px;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          font-size: 14px;
          font-weight: bold;
          transition: opacity 0.2s;
        }

        .print-actions button:hover {
          opacity: 0.8;
        }

        .btn-print {
          background: #4CAF50;
          color: white;
        }

        .btn-close {
          background: #f44336;
          color: white;
        }

        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          body {
            width: 58mm;
            margin: 0;
            padding: 0;
            background: white;
          }

          .receipt-container {
            width: 100%;
            padding: 2mm;
          }

          .print-actions {
            display: none !important;
          }

          .observation {
            background: transparent;
            border: 1px dashed #000;
          }

          .address-section {
            background: transparent;
            border: 1px dashed #000;
          }

          .coupon-badge {
            background: transparent;
            color: #000;
            border: 1px solid #000;
          }

          @page {
            size: 58mm auto;
            margin: 0;
          }
        }

        @media screen {
          body {
            background: #f0f0f0;
            padding: 10px;
          }

          .receipt-container {
            background: white;
            padding: 5mm;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
          }
        }
      `}</style>

      <div className="receipt-container">
        {/* Cabeçalho */}
        <div className="header">
          <h1>BONFIM GOURMET</h1>
          <h2>Comanda de Pedido</h2>
        </div>

        <div className="separator"></div>

        {/* Informações do Pedido */}
        <div className="section">
          <div className="info-line">
            <span className="info-label">Pedido:</span>
            <span className="info-value">#{order.code}</span>
          </div>
          <div className="info-line">
            <span className="info-label">Data:</span>
            <span className="info-value">{formatDate(order.created_at)}</span>
          </div>
          <div className="info-line">
            <span className="info-label">Status:</span>
            <span className="info-value">{order.status || "N/A"}</span>
          </div>
        </div>

        <div className="separator"></div>

        {/* Informações do Cliente */}
        <div className="section">
          <div className="section-title">Cliente</div>
          <div className="info-line">
            <span className="info-label">Nome:</span>
            <span className="info-value">
              {order.client?.name || "Anônimo"}
            </span>
          </div>
          <div className="info-line">
            <span className="info-label">Contato:</span>
            <span className="info-value">{order.client?.contact || "N/A"}</span>
          </div>
        </div>

        {/* Endereço de Entrega */}
        {addressLines && addressLines.length > 0 && (
          <>
            <div className="dashed"></div>
            <div className="section">
              <div className="section-title">Endereço de Entrega</div>
              <div className="address-section">
                {addressLines.map((line, idx) => (
                  <div key={idx} className="address-line">
                    {line}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="separator"></div>

        {/* Produtos */}
        <div className="section">
          <div className="section-title">Itens do Pedido</div>
          {order.products?.map((product, idx) => (
            <div key={idx} className="product-item">
              <div className="product-name">
                {idx + 1}. {product.product?.name || "Produto"}
              </div>
              <div className="product-detail">
                Quantidade: {product.quantity}
              </div>
              <div className="product-detail">
                Valor Unit.: R${" "}
                {(parseFloat(product.total_value) / product.quantity).toFixed(
                  2
                )}
              </div>
              <div className="product-detail">
                <strong>
                  Total: R$ {parseFloat(product.total_value).toFixed(2)}
                </strong>
              </div>

              {/* Produtos Selecionados */}
              {product.selected_produtos &&
                product.selected_produtos.length > 0 && (
                  <div className="product-detail">
                    <div style={{ marginTop: "5px", fontWeight: "bold" }}>
                      Produtos Adicionais:
                    </div>
                    {product.selected_produtos.map((prod, pidx) => (
                      <div key={pidx} className="sub-item">
                        • {prod.name} x{prod.qty} - R${" "}
                        {(prod.price * prod.qty).toFixed(2)}
                      </div>
                    ))}
                  </div>
                )}

              {/* Ingredientes Selecionados */}
              {product.selected_ingredients &&
                product.selected_ingredients.length > 0 && (
                  <div className="product-detail">
                    <div style={{ marginTop: "5px", fontWeight: "bold" }}>
                      Ingredientes:
                    </div>
                    {product.selected_ingredients.map((ing, iidx) => (
                      <div key={iidx} className="sub-item">
                        • {ing.name}
                      </div>
                    ))}
                  </div>
                )}

              {/* Observação */}
              {product.observation && (
                <div className="observation">
                  <strong>OBS:</strong> {product.observation}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="separator"></div>

        {/* Totais */}
        <div className="section totals">
          <div className="total-line">
            <span>Subtotal Produtos:</span>
            <span>R$ {productsSubtotal.toFixed(2)}</span>
          </div>

          {order.coupon_code && (
            <div className="total-line">
              <span>
                Desconto
                <span className="coupon-badge">
                  {order.coupon_code} (
                  {parseFloat(order.discount_percentage || 0).toFixed(2)}%)
                </span>
              </span>
              <span>- R$ {discountAmount.toFixed(2)}</span>
            </div>
          )}

          {freightValue > 0 && (
            <div className="total-line">
              <span>Taxa de Delivery:</span>
              <span>R$ {freightValue.toFixed(2)}</span>
            </div>
          )}

          <div className="total-line highlight">
            <span>TOTAL:</span>
            <span>R$ {total.toFixed(2)}</span>
          </div>
        </div>

        <div className="separator"></div>

        {/* Informações de Pagamento */}
        <div className="section">
          <div className="info-line">
            <span className="info-label">Forma de Pagamento:</span>
            <span className="info-value">{order.pay_method || "N/A"}</span>
          </div>
          <div className="info-line">
            <span className="info-label">Tipo de Entrega:</span>
            <span className="info-value">{order.delivery_type || "N/A"}</span>
          </div>
          {order.payed && (
            <div className="info-line">
              <span className="info-label">Pagamento:</span>
              <span className="info-value">✓ PAGO</span>
            </div>
          )}
        </div>

        <div className="separator"></div>

        {/* Rodapé */}
        <div className="footer">
          <p>Obrigado pela preferência!</p>
          <p>BONFIM GOURMET</p>
        </div>
      </div>

      {/* Botões de ação (não aparecem na impressão) */}
      <div className="print-actions">
        <button className="btn-print" onClick={() => window.print()}>
          🖨️ Reimprimir
        </button>
        <button className="btn-close" onClick={() => window.close()}>
          ✕ Fechar
        </button>
      </div>
    </>
  );
};

export default PrintOrderReceipt;
